// RH-PAIE-2D §5 — Dictionnaire centralisé des libellés humains pour les codes techniques de
// paie (paramètres, rubriques, grille salariale). Les codes techniques restent la source de
// vérité en base et dans l'API — ce fichier ne fait que les traduire pour l'affichage. Ne pas
// dupliquer ces libellés ailleurs : importer depuis ce fichier partout où un code de ce type
// est affiché à l'utilisateur.
//
// Convention : chaque dictionnaire est un objet { CODE: 'Libellé humain' }. Les fonctions
// humaniser*() renvoient le code lui-même si aucune correspondance n'existe (jamais un texte
// vide ou "undefined" à l'écran).

export const STATUTS_PAIE = {
  BROUILLON: 'Brouillon',
  A_VALIDER: 'À valider',
  VALIDE: 'Validé',
  PROGRAMME: 'Programmé',
  ACTIF: 'Actif',
  ARCHIVE: 'Archivé',
  ANNULE: 'Annulé',
};

// Statut spécial : un paramètre/une rubrique dont le niveau de confiance est INTERDIT_DE_CALCUL
// n'est jamais utilisable, quel que soit son statut par ailleurs — affiché comme un badge à part.
export const LIBELLE_INTERDIT_DE_CALCUL = 'Non utilisable pour le calcul';

export const SOURCES_PAIE = {
  TEXTE_LEGAL: 'Texte légal',
  DECRET: 'Décret',
  ARRETE: 'Arrêté',
  CONVENTION_COLLECTIVE: 'Convention collective',
  ACCORD_SOGAS: 'Accord SOGAS',
  DECISION_DIRECTION: 'Décision de Direction',
  CONTRAT_INDIVIDUEL: 'Contrat individuel',
  PARAMETRE_IPM: 'Paramètre IPM',
  REPRISE_EXISTANT: 'Donnée reprise de l\'ancien système',
  A_CONFIRMER: 'À confirmer',
};

export const NIVEAUX_CONFIANCE_PAIE = {
  CONFIRME_OFFICIEL: 'Confirmé par une source officielle',
  CONFIRME_SOGAS: 'Confirmé par un document SOGAS',
  A_VALIDER_DRH: 'À valider par la DRH',
  A_VALIDER_COMPTABILITE: 'À valider par la comptabilité',
  A_VALIDER_JURISTE: 'À valider par le juriste',
  A_VALIDER_DGID: 'À valider par la DGID',
  A_VALIDER_CSS: 'À valider par la CSS',
  A_VALIDER_IPRES: 'À valider par l\'IPRES',
  INTERDIT_DE_CALCUL: LIBELLE_INTERDIT_DE_CALCUL,
};

export const TYPES_VALEUR_PARAMETRE = {
  MONTANT: 'Montant',
  POURCENTAGE: 'Pourcentage',
  PLAFOND: 'Plafond',
  PLANCHER: 'Plancher',
  DUREE: 'Durée',
  SEUIL: 'Seuil',
  COEFFICIENT: 'Coefficient',
  BOOLEEN: 'Oui / Non',
  REFERENCE: 'Référence',
  BAREME: 'Barème',
  TRANCHE: 'Tranche',
  HEURE_JOUR: 'Heure du jour',
};

export const DOMAINES_PAIE = {
  TEMPS: 'Temps de travail',
  REMUNERATION: 'Rémunération',
  RETENUES: 'Retenues',
  PRETS: 'Prêts',
  CONGES: 'Congés',
  COTISATIONS_SOCIALES: 'Cotisations sociales',
  FISCALITE: 'Fiscalité',
  GRILLE_SALARIALE: 'Grille salariale',
  INDEMNITES: 'Indemnités',
  AVANTAGES: 'Avantages',
  REGULARISATIONS: 'Régularisations',
};

// RH-PAIE-2E §4 — famille professionnelle : clé fonctionnelle obligatoire de toute entrée de
// grille salariale (jamais la seule catégorie, ambiguë entre familles).
export const FAMILLES_PROFESSIONNELLES = {
  OUVRIERS: 'Ouvriers',
  EMPLOYES: 'Employés',
  AGENTS_MAITRISE: 'Agents de maîtrise',
  CADRES: 'Cadres',
};
// 'A_CLASSER' n'existe que côté référentiel des catégories historiques non reclassées —
// jamais une valeur valide pour une nouvelle entrée de grille elle-même.
export const FAMILLES_CATEGORIES_AVEC_NON_CLASSE = { ...FAMILLES_PROFESSIONNELLES, A_CLASSER: 'À classer par la DRH' };

export const UNITES_PAIE = {
  FCFA: 'FCFA',
  POURCENTAGE: 'Pourcentage',
  HEURES_SEMAINE: 'Heures par semaine',
  HEURES: 'Heures',
  JOURS: 'Jours',
  COEFFICIENT: 'Coefficient',
  NOMBRE: 'Nombre',
  BOOLEEN: 'Oui / Non',
  SANS_UNITE: 'Sans unité',
  MINUTES: 'Minutes',
};

export const TYPES_RUBRIQUE = {
  GAIN: 'Gain',
  RETENUE: 'Retenue',
  COTISATION_SALARIALE: 'Cotisation salariale',
  COTISATION_PATRONALE: 'Cotisation patronale',
  INFORMATION: 'Information',
};

export const FAMILLES_RUBRIQUE = {
  SALAIRE_BASE: 'Salaire de base',
  SURSALAIRE: 'Sursalaire',
  PRIME: 'Prime',
  INDEMNITE: 'Indemnité',
  HEURE_SUPPLEMENTAIRE: 'Heures supplémentaires',
  ABSENCE: 'Absence',
  CONGE: 'Congé',
  IPRES: 'IPRES',
  CSS: 'CSS',
  IPM: 'IPM',
  FISCALITE: 'Fiscalité',
  PRET: 'Prêt',
  AVANCE: 'Avance',
  DETTE: 'Dette',
  SAISIE: 'Saisie-arrêt',
  AVANTAGE_NATURE: 'Avantage en nature',
  REGULARISATION: 'Régularisation',
  RAPPEL: 'Rappel',
  DEPART: 'Départ',
  INFORMATION_CUMUL: 'Information / cumul',
};

export const MODES_CALCUL_RUBRIQUE = {
  FORMULE: 'Formule',
  SAISIE_MANUELLE: 'Saisie manuelle',
  REFERENCE_MODULE: 'Référence à un autre module',
};

export const TYPES_DOCUMENT_JUSTIFICATIF = {
  TEXTE_LEGAL: 'Texte légal',
  DECRET: 'Décret',
  ARRETE: 'Arrêté',
  CONVENTION_COLLECTIVE: 'Convention collective',
  ACCORD_SOGAS: 'Accord SOGAS',
  DECISION_DIRECTION: 'Décision de Direction',
  NOTE_SERVICE: 'Note de service',
  CONTRAT_INDIVIDUEL: 'Contrat individuel',
  AUTRE: 'Autre',
};

export const ARRONDIS_GRILLE = {
  AUCUN: 'Aucun arrondi',
  DIZAINE: 'Dizaine la plus proche',
  CENTAINE: 'Centaine la plus proche',
  MILLIER: 'Millier le plus proche',
};

function creerHumaniseur(dictionnaire) {
  return (code) => (code ? (dictionnaire[code] || code) : '–');
}

export const humaniserStatut = creerHumaniseur(STATUTS_PAIE);
export const humaniserSource = creerHumaniseur(SOURCES_PAIE);
export const humaniserNiveauConfiance = creerHumaniseur(NIVEAUX_CONFIANCE_PAIE);
export const humaniserTypeValeur = creerHumaniseur(TYPES_VALEUR_PARAMETRE);
export const humaniserDomaine = creerHumaniseur(DOMAINES_PAIE);
export const humaniserUnite = creerHumaniseur(UNITES_PAIE);
export const humaniserTypeRubrique = creerHumaniseur(TYPES_RUBRIQUE);
export const humaniserFamilleRubrique = creerHumaniseur(FAMILLES_RUBRIQUE);
export const humaniserModeCalcul = creerHumaniseur(MODES_CALCUL_RUBRIQUE);
export const humaniserTypeDocument = creerHumaniseur(TYPES_DOCUMENT_JUSTIFICATIF);
export const humaniserArrondi = creerHumaniseur(ARRONDIS_GRILLE);
export const humaniserFamilleProfessionnelle = creerHumaniseur(FAMILLES_CATEGORIES_AVEC_NON_CLASSE);

// Couleurs de badge par statut — un seul endroit pour toute l'app (paramètres, rubriques, grille).
export const VERT = '#1B5E20';
export const ROUGE = '#C62828';
export const ORANGE = '#E65100';
export const GRIS = '#666';
export const COULEUR_STATUT_PAIE = {
  BROUILLON: '#9E9E9E',
  A_VALIDER: ORANGE,
  VALIDE: VERT,
  PROGRAMME: '#1565C0',
  ACTIF: VERT,
  ARCHIVE: GRIS,
  ANNULE: ROUGE,
};
export const COULEUR_INTERDIT_DE_CALCUL = ROUGE;
